/**
 * ==================================================================================
 * CENTRALIZED AUTHENTICATION HOOK
 * ==================================================================================
 * 
 * PURPOSE:
 * Provides consistent authentication state and utilities across all components.
 * Eliminates the need for components to directly import auth or handle loading states.
 * 
 * USAGE:
 * const { user, isLoading, isAuthenticated, requireAuth } = useAuth();
 * 
 * BENEFITS:
 * - Single source of truth for auth state
 * - Consistent loading state handling
 * - Easy to modify auth behavior globally
 * - Better error handling and user experience
 * ==================================================================================
 */

import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Computed values
  const isAuthenticated = !!user;
  const userId = user?.uid || null;
  const userEmail = user?.email || null;
  const emailVerified = !!user?.emailVerified;
  const providerIds = Array.isArray(user?.providerData) ? user.providerData.map(p => p?.providerId).filter(Boolean) : [];
  const displayName = user?.displayName || user?.email || 'User';

  // Utility functions
  // UI-agnostic check to ensure the user is authenticated before running an action.
  // Usage: requireAuth(() => doSomething(), () => navigate('/login'))
  const requireAuth = (callback, onFail) => {
    if (!isAuthenticated) {
      if (typeof onFail === 'function') onFail();
      return false;
    }
    if (typeof callback === 'function') callback();
    return true;
  };

  // Convenience wrapper for sign-out to avoid importing firebase auth in components
  const signOutUser = async () => {
    await signOut(auth);
  };

  const isOwner = (resourceUserId) => {
    return isAuthenticated && userId === resourceUserId;
  };

  return {
    // Core auth state
    user,
    isLoading,
    isAuthenticated,
    
    // Convenient user properties
    userId,
    userEmail,
    displayName,
    emailVerified,
    providerIds,
    
    // Utility functions
    requireAuth,
    isOwner,
    signOutUser,
    
    // Compatibility alias
    authIsLoading: isLoading
  };
};

export default useAuth;
